import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, Edit2, Trash2, Users, Mail, Phone, MapPin } from "lucide-react";
import api from "@/lib/api";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"
];

const PAYMENT_TERMS = ["none", "Due on Receipt", "Net 15", "Net 30", "Net 45", "Net 60", "Net 90"];

export default function CustomersPage({ companyId }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [notes, setNotes] = useState("");

  const loadCustomers = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : "";
      const res = await api.get(`/companies/${companyId}/customers${params}`);
      setCustomers(res.data.customers || []);
    } catch (err) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [companyId, searchQuery]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhone("");
    setStreetAddress("");
    setCity("");
    setState("");
    setZipCode("");
    setPaymentTerms("none");
    setNotes("");
    setEditingCustomer(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setName(customer.name || "");
    setEmail(customer.email || "");
    setPhone(customer.phone || "");
    setStreetAddress(customer.street_address || "");
    setCity(customer.city || "");
    setState(customer.state || "");
    setZipCode(customer.zip_code || "");
    setPaymentTerms(customer.payment_terms || "none");
    setNotes(customer.notes || "");
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        street_address: streetAddress.trim(),
        city: city.trim(),
        state: state,
        zip_code: zipCode.trim(),
        payment_terms: paymentTerms === "none" ? "" : paymentTerms,
        notes: notes.trim()
      };
      
      if (editingCustomer) {
        await api.put(`/companies/${companyId}/customers/${editingCustomer.customer_id}`, data);
        toast.success("Customer updated");
      } else {
        await api.post(`/companies/${companyId}/customers`, data);
        toast.success("Customer created");
      }
      
      setShowDialog(false);
      resetForm();
      loadCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save customer");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customer) => {
    if (!confirm(`Delete customer "${customer.name}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/companies/${companyId}/customers/${customer.customer_id}`);
      toast.success("Customer deleted");
      loadCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete customer");
    }
  };

  const formatAddress = (customer) => {
    const parts = [];
    if (customer.city) parts.push(customer.city);
    if (customer.state) parts.push(customer.state);
    if (customer.zip_code) parts.push(customer.zip_code);
    return parts.join(", ") || "-";
  };

  if (!companyId) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Select a company to view customers
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#d4c4b0]">Customers</h1>
          <p className="text-[#8b9299] text-sm mt-1">Manage your clients for invoicing</p>
        </div>
        <Button onClick={openNewDialog} className="btn-primary-gradient" data-testid="add-customer-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Customer
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Total Customers</p>
                <p className="text-xl font-bold text-[#d4c4b0]">{customers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer List */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <CardTitle className="text-sm font-medium text-[#d4c4b0]">All Customers</CardTitle>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[#6b7280]" />
              <Input
                placeholder="Search customers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64 bg-secondary border-border text-[#d4c4b0] h-9"
                data-testid="search-customers"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-6 h-6 border-2 border-[#e85d24] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : customers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-[#6b7280]">
              <Users className="w-12 h-12 mb-3 opacity-50" />
              <p>No customers found</p>
              <Button variant="link" onClick={openNewDialog} className="text-[#e85d24] mt-2">
                Add your first customer
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-[#8b9299]">Name</TableHead>
                    <TableHead className="text-[#8b9299]">Email</TableHead>
                    <TableHead className="text-[#8b9299]">Phone</TableHead>
                    <TableHead className="text-[#8b9299]">Location</TableHead>
                    <TableHead className="text-[#8b9299] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((customer) => (
                    <TableRow key={customer.customer_id} className="border-border hover:bg-secondary/50" data-testid={`customer-row-${customer.customer_id}`}>
                      <TableCell className="text-[#d4c4b0] font-medium">{customer.name}</TableCell>
                      <TableCell className="text-[#8b9299]">
                        {customer.email ? (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {customer.email}
                          </span>
                        ) : "-"}
                      </TableCell>
                      <TableCell className="text-[#8b9299]">
                        {customer.phone ? (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {customer.phone}
                          </span>
                        ) : "-"}
                      </TableCell>
                      <TableCell className="text-[#8b9299]">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {formatAddress(customer)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8b9299] hover:text-[#d4c4b0]" onClick={() => openEditDialog(customer)} data-testid={`edit-customer-${customer.customer_id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDelete(customer)} data-testid={`delete-customer-${customer.customer_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">
              {editingCustomer ? "Edit Customer" : "Add Customer"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <div>
              <Label className="text-[#8b9299] text-sm">Customer Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe or Company Inc." className="mt-1.5 bg-secondary border-border text-white" data-testid="customer-name-input" />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#8b9299] text-sm">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@email.com" className="mt-1.5 bg-secondary border-border text-white" data-testid="customer-email-input" />
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">Phone</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" className="mt-1.5 bg-secondary border-border text-white" data-testid="customer-phone-input" />
              </div>
            </div>

            <div>
              <Label className="text-[#8b9299] text-sm">Street Address</Label>
              <Input value={streetAddress} onChange={(e) => setStreetAddress(e.target.value)} placeholder="123 Client Street" className="mt-1.5 bg-secondary border-border text-white" data-testid="customer-street-input" />
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-2">
                <Label className="text-[#8b9299] text-sm">City</Label>
                <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" className="mt-1.5 bg-secondary border-border text-white" data-testid="customer-city-input" />
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">State</Label>
                <Select value={state} onValueChange={setState}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="customer-state-select">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-48">
                    {US_STATES.map(s => (
                      <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#8b9299] text-sm">ZIP</Label>
                <Input value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="12345" className="mt-1.5 bg-secondary border-border text-white" data-testid="customer-zip-input" />
              </div>
            </div>

            <div>
              <Label className="text-[#8b9299] text-sm">Payment Terms (Override)</Label>
              <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                <SelectTrigger className="mt-1.5 bg-secondary border-border text-white" data-testid="customer-terms-select">
                  <SelectValue placeholder="Use company default" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {PAYMENT_TERMS.map(t => (
                    <SelectItem key={t} value={t} className="text-white">{t === "none" ? "Use company default" : t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#8b9299] text-sm">Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes about this customer..." className="mt-1.5 bg-secondary border-border text-white" rows={2} data-testid="customer-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-border text-white">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !name.trim()} className="btn-primary-gradient" data-testid="save-customer-btn">
              {saving ? "Saving..." : (editingCustomer ? "Update" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
